import React, { useEffect } from 'react'
import { Link } from '@inertiajs/react'
import { motion } from 'framer-motion'
import confetti from 'canvas-confetti'
import UserLayout from '../../Layouts/UserLayout'
import useTranslation from '../../hooks/useTranslation'

export default function Show({ session, xp_breakdown }) {
  const { t } = useTranslation()
  const { text, score, max_score, penalty_count } = session
  
  // Define success threshold (50%)
  const percentage = max_score > 0 ? (score / max_score) : 1
  // Ensure we compare numbers, not strings from JSON serialization
  const isSuccess = percentage >= 0.5 && score > 0
  const isPerfect = percentage === 1

  useEffect(() => {
    if (!isSuccess) return

    // Fire confetti on mount only if successful
    const duration = 3000
    const end = Date.now() + duration

    const frame = () => {
      confetti({
        particleCount: 2,
        angle: 60,
        spread: 55,
        origin: { x: 0 },
        colors: ['#059669', '#34D399', '#10B981'] // Emerald shades
      })
      confetti({
        particleCount: 2,
        angle: 120,
        spread: 55,
        origin: { x: 1 },
        colors: ['#059669', '#34D399', '#10B981']
      })

      if (Date.now() < end) {
        requestAnimationFrame(frame)
      }
    }
    frame()
  }, [isSuccess])

  // Dynamic Styles based on result
  const theme = isSuccess 
    ? {
        bg: "bg-gradient-to-r from-emerald-500 to-teal-500",
        border: "border-emerald-100",
        text: "text-emerald-600",
        subtext: "text-emerald-100",
        title: isPerfect ? t('session.perfect') : t('session.wonderful'),
        subtitle: t('session.completed'),
        button: "bg-emerald-600 hover:bg-emerald-700 shadow-emerald-200"
      }
    : {
        bg: "bg-gradient-to-r from-slate-500 to-gray-500",
        border: "border-gray-100",
        text: "text-slate-600",
        subtext: "text-gray-200",
        title: t('session.nice_try'),
        subtitle: t('session.keep_practicing'),
        button: "bg-slate-600 hover:bg-slate-700 shadow-slate-200"
      }

  return (
    <UserLayout>
      <div className="max-w-2xl mx-auto py-12 px-4">
        <motion.div 
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className={`bg-white rounded-3xl shadow-xl overflow-hidden border ${theme.border}`}
        >
          {/* Header Banner */}
          <div className={`${theme.bg} p-8 text-center relative overflow-hidden transition-colors duration-500`}>
             <div className="absolute top-0 left-0 w-full h-full opacity-10">
                <svg width="100%" height="100%">
                  <pattern id="pattern" x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
                    <circle cx="2" cy="2" r="2" fill="white" />
                  </pattern>
                  <rect width="100%" height="100%" fill="url(#pattern)" />
                </svg>
             </div>
             
             <motion.h1 
               initial={{ y: 20, opacity: 0 }}
               animate={{ y: 0, opacity: 1 }}
               transition={{ delay: 0.2 }}
               className="text-4xl font-extrabold text-white mb-2 relative z-10"
             >
               {theme.title}
             </motion.h1>
             <p className={`${theme.subtext} font-medium relative z-10`}>{theme.subtitle}</p>
          </div>

          <div className="p-8">
            {/* Total XP Circle */}
            <div className="flex justify-center -mt-16 mb-8 relative z-20">
              <motion.div 
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 200, delay: 0.4 }}
                className={`w-32 h-32 bg-white rounded-full flex items-center justify-center shadow-lg border-4 ${isSuccess ? 'border-emerald-100' : 'border-gray-100'}`}
              >
                 <div className="text-center">
                    <div className="text-xs text-gray-400 font-bold uppercase tracking-wider">{t('session.total_xp')}</div>
                    <div className={`text-4xl font-black ${theme.text}`}>+{xp_breakdown.total}</div>
                 </div>
              </motion.div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 gap-4 mb-8">
              <StatCard 
                label={t('session.quiz_score')} 
                value={`${score} / ${max_score}`} 
                delay={0.6}
                color={score === max_score ? "text-emerald-600" : (isSuccess ? "text-gray-800" : "text-rose-600")}
              />
              <StatCard 
                label={t('session.penalties')} 
                value={penalty_count} 
                delay={0.7}
                color="text-rose-500"
              />
            </div>

            {/* XP Breakdown Details */}
            <motion.div 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
              className="bg-gray-50 rounded-xl p-6 mb-8 space-y-3"
            >
              <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-4">{t('session.xp_breakdown')}</h3>
              <div className="flex justify-between items-center text-sm">
                <span className="text-gray-600">{t('session.base_xp')} {text.difficulty})</span>
                <span className="font-mono font-medium">{xp_breakdown.base}</span>
              </div>
              <div className="flex justify-between items-center text-sm">
                <span className="text-gray-600">{t('session.accuracy_bonus')}</span>
                <span className={`font-mono font-medium ${isSuccess ? 'text-emerald-600' : 'text-gray-500'}`}>+{xp_breakdown.bonus}</span>
              </div>
              <div className="h-px bg-gray-200 my-2"></div>
              <div className="flex justify-between items-center font-bold text-lg">
                <span>{t('session.total_earned')}</span>
                <span className={theme.text}>{xp_breakdown.total}</span>
              </div>
            </motion.div>

            {/* Actions */}
            <div className="space-y-3">
              <Link 
                href="/texts"
                className={`block w-full text-white text-center py-3 rounded-xl font-bold transition-transform hover:scale-[1.02] shadow-md ${theme.button}`}
              >
                {t('session.read_another')}
              </Link>
              <Link 
                href="/dashboard"
                className="block w-full bg-white text-gray-600 text-center py-3 rounded-xl font-semibold border border-gray-200 hover:bg-gray-50"
              >
                {t('session.go_dashboard')}
              </Link>
            </div>

          </div>
        </motion.div>
      </div>
    </UserLayout>
  )
}

function StatCard({ label, value, delay, color }) {
  return (
    <motion.div 
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay }}
      className="bg-gray-50 p-4 rounded-xl text-center border border-gray-100"
    >
      <div className="text-sm text-gray-500 mb-1">{label}</div>
      <div className={`text-2xl font-bold ${color}`}>{value}</div>
    </motion.div>
  )
}








